"use client";

import { useEffect, useMemo, useState } from "react";
import { motion } from "framer-motion";
import { useNavigate } from "@tanstack/react-router";

import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { DotPattern } from "@/components/ui/dot-pattern";
import { ChatwootWidget } from "@/components/ui/chatwoot-widget";
import { ThemeToggle } from "@/components/theme-toggle";
import { Logo } from "@/components/ui/logo";
// import { BookCoverImage } from "@/components/ui/book-cover-image";
import { BookCard as SimpleBookCard } from "@/components/ui/book-card";
import { CreateBookCard } from "@/components/ui/book-card-create";
import { DashboardSkeleton } from "@/components/ui/dashboard-skeleton";
import { Input } from "@/components/ui/input";


import { cn, reorderServicesForProgress } from "@/lib/utils";
import {
  BookOpen,
  LogOut,
  AlertCircle,
  Loader2,
  Search,
  X
} from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useLivros } from "@/hooks/useLivros";


interface DashboardRealProps {
  onLogout: () => void;
}

export function DashboardReal({ onLogout }: DashboardRealProps) {
  const { cliente, logout, loading: authLoading } = useAuth();
  const { livros, loading: livrosLoading, error, buscarLivrosDisponiveis } = useLivros(cliente?.id);
  const navigate = useNavigate();

  // Local search state and derived lists (hooks must stay above any early returns)
  const [searchTerm, setSearchTerm] = useState("");
  const handleClearSearch = () => setSearchTerm("");

  const livrosEntregues = useMemo(() => livros.filter(l => l.status_entrega), [livros]);

  const livrosFiltrados = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return livrosEntregues;
    return livrosEntregues.filter((l) => {
      const titulo = (l.titulo ?? "").toString().toLowerCase();
      const subtituloRaw = (l as unknown as Record<string, unknown>)["subtitulo"];
      const subtitulo = (typeof subtituloRaw === "string" || typeof subtituloRaw === "number") ? String(subtituloRaw).toLowerCase() : "";
      const autor = (l.nome_autor ?? "").toString().toLowerCase();
      return titulo.includes(q) || subtitulo.includes(q) || autor.includes(q);
    });
  }, [livrosEntregues, searchTerm]);


  // Link externo para gerar um novo livro (TODO: substituir pelo link real)
  const externalGerarLivroUrl = "https://bot.responda-ai.com/gerar-mais-livros";

  // Loading geral: auth ainda carregando OU livros carregando pela primeira vez
  const isInitialLoading = authLoading || (livrosLoading && livros.length === 0);

  // Nota: intentionally omitting buscarLivrosDisponiveis from deps because the hook returns
  // a new function identity each render (not memoized), which would cause this effect to
  // re-run continuously. We only want to trigger when auth state or cliente.id changes.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (cliente?.id && !authLoading) {
      // Apenas livros entregues, mas só depois que auth terminar
      buscarLivrosDisponiveis();
    }
  }, [cliente?.id, authLoading]);

  const handleLogout = () => {
    logout();
    onLogout();
  };

  // Download agora apenas dentro de detalhes; removido do card simplificado



  // Loading inicial completo
  if (isInitialLoading) {
    return <DashboardSkeleton />;
  }

  if (!cliente) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center space-y-4">
          <AlertCircle className="w-8 h-8 mx-auto text-red-500" />
          <p className="text-muted-foreground">Cliente não encontrado</p>
        </div>
      </div>
    );
  }




  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      {/* Background Pattern */}
      <DotPattern
        className={cn(
          "[mask-image:radial-gradient(800px_circle_at_center,white,transparent)]",
          "fixed inset-0 h-full w-full opacity-20 dark:opacity-50"
        )}
      />

      {/* Header */}
      <header className="relative z-10 border-b border-border/50 bg-background/95 backdrop-blur-sm">
        <div className="container mx-auto px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="h-8 xs:h-9 sm:h-10">
                <Logo className="h-8 xs:h-9 sm:h-10 mx-0" />
              </div>
              <div>
                <h1 className="text-lg sm:text-xl">Sabhia - HUB</h1>
                <p className="text-xs xs:text-sm text-muted-foreground">
                  Bem-vindo, {cliente?.nome}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <ThemeToggle />
              <Button
                variant="outline"
                onClick={handleLogout}
                className="gap-2 px-3 sm:px-4 text-xs sm:text-sm min-h-10"
              >
                <LogOut className="w-4 h-4" />
                Sair
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="relative z-10 container mx-auto px-4 py-6 sm:py-8">
        {/* Filtros removidos: mostrar apenas entregues */}
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-2">Sua biblioteca</h2>

          <div className="relative w-full sm:max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4" />
            <Input
              value={searchTerm}
              onChange={(e: any) => setSearchTerm(e.target.value)}
              placeholder="Pesquisar por título ou autor..."
              className="pl-10 pr-10 w-full max-w-md"
            />
            {searchTerm && (
              <X
                onClick={handleClearSearch}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 text-muted-foreground w-4 h-4 cursor-pointer hover:text-foreground"
              />
            )}
          </div>

          {/* Loading sutil quando recarregando */}
          {livrosLoading && livros.length > 0 && (
            <div className="flex items-center gap-2 text-xs sm:text-sm text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span>Atualizando...</span>
            </div>
          )}
        </div>

        {/* Lista de Livros */}
        {(() => {
          if (error) {
            return (
              <Card>
                <CardContent className="px-4 py-6 sm:p-8 text-center">
                  <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg mb-2">Erro ao carregar livros</h3>
                  <p className="text-xs xs:text-sm text-muted-foreground mb-4">{error}</p>
                  <Button onClick={() => window.location.reload()}>
                    Tentar Novamente
                  </Button>
                </CardContent>
              </Card>
            );
          }

          if (livrosEntregues.length === 0) {
            return (
              <Card>
                <CardContent className="px-4 py-6 sm:p-8 text-center">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg mb-2">Nenhum livro encontrado</h3>
                  <p className="text-xs xs:text-sm text-muted-foreground">
                    Você ainda não tem livros disponíveis para download.
                  </p>
                </CardContent>
              </Card>
            );
          }

          if (searchTerm.trim().length > 0 && livrosFiltrados.length === 0) {
            return (
              <Card>
                <CardContent className="px-4 py-6 sm:p-8 text-center">
                  <BookOpen className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <h3 className="text-base sm:text-lg mb-2">Nenhum resultado para “{searchTerm}”</h3>
                  <p className="text-xs xs:text-sm text-muted-foreground mb-4">Não encontramos resultados para “{searchTerm}”. Tente ajustar sua busca por título, subtítulo ou autor.</p>
                  <Button variant="ghost" size="sm" onClick={handleClearSearch}>Limpar pesquisa</Button>
                </CardContent>
              </Card>
            );
          }

          return (
            <motion.div
              className="grid grid-cols-dashboard-1 sm:grid-cols-dashboard-2 lg:grid-cols-dashboard-3 xl:grid-cols-dashboard-4 gap-4 sm:gap-6"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.5 }}
            >
              {livrosFiltrados.map((livro, index) => (
                <motion.div
                  key={livro.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{
                    delay: index * 0.1,
                    duration: 0.4,
                    ease: "easeOut"
                  }}
                >
                  {(() => {
                    const { percent, statusByKey } = reorderServicesForProgress(livro)
                    if (livro.status_entrega && !statusByKey.livro) {
                      console.warn('[DashboardReal] Delivered book with statusByKey.livro=false', { id_pedido: (livro as any).id_pedido, id: livro.id })
                    }

                    return (
                      <SimpleBookCard
                        title={livro.titulo}
                        author={livro.nome_autor ?? ""}
                        coverImage={livro.capa?.imagem_capa_png || livro.capa?.imagem_capa || ""}
                        progress={percent}
                        onClick={() => { if (!livro.id_pedido) return; navigate({ to: '/livro/$id_pedido', params: { id_pedido: String(livro.id_pedido) } }) }}
                      />
                    )
                  })()}

                </motion.div>
              ))}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, ease: "easeOut" }}
              >
                <CreateBookCard href={externalGerarLivroUrl} />
              </motion.div>

            </motion.div>
          );
        })()}
      </main>

      {/* WhatsApp Float */}
      <ChatwootWidget />
    </div>
  );
}